import * as React from 'react';
import { observer } from 'mobx-react-lite';
import setErrorDialogState from '../mutators/setErrorDialogState';
import getStore from '../store';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { Dialog, DialogFooter, DialogType } from '@fluentui/react/lib/Dialog';
import {
    OWAConnectedAccountState,
    getAccountsInErrorState,
    getAccountsInState,
} from 'owa-accounts-store';
import loc from 'owa-localize';
import {
    accountNotAvailable,
    failedToConnect_tryagain_Message,
    failedToConnect_reconnect_Message,
    accountNotAvailableTryAgainLabel,
    accountNotAvailableFixAccountLabel,
    failedToConnect_reconnect_MessageEdu,
    failedToConnect_tryagain_MessageEdu,
} from './OWAAccountsLoadError.locstring.json';
import {
    lazyNavigateToAddCalendarPanel,
    lazyOpenAddCalendarsPane,
    AddCalendarsMenuItemId,
} from 'owa-calendar-addcalendar';
import { lazyGetAndUpdateAccessToken } from 'owa-headers-refresh-token';
import { logUsage } from 'owa-analytics';
import { accountErrorReferral } from 'owa-accounts-editor';
import { isEduStudent } from 'owa-bpos-utils';
import { getHasUserRetried } from '../selectors/getHasUserRetried';

const styles = require('./OWAAccountsLoadError.scss');

export type DialogUserResponse = 'Dismiss' | 'OpenAddAccountsOption' | 'FixTokenError';

const OWAAccountsLoadError = observer(function OWAAccountsLoadError() {
    const { isDialogHidden, failureState } = getStore();
    const isTokenError =
        failureState == OWAConnectedAccountState.TransientTokenError ||
        failureState == OWAConnectedAccountState.UnknownTokenError;

    const failedToConnectMessage =
        isTokenError && isEduStudent()
            ? loc(failedToConnect_tryagain_MessageEdu)
            : isTokenError && !isEduStudent()
            ? loc(failedToConnect_tryagain_Message)
            : isEduStudent()
            ? loc(failedToConnect_reconnect_MessageEdu)
            : loc(failedToConnect_reconnect_Message);

    return (
        <Dialog
            hidden={isDialogHidden}
            onDismiss={closeErrorDialog}
            dialogContentProps={{
                type: DialogType.normal,
                title: loc(accountNotAvailable),
                subText: failedToConnectMessage,
            }}
            modalProps={{ isBlocking: false }}
            styles={{ main: styles.errorModal }}>
            <DialogFooter>
                {isTokenError && !getHasUserRetried() && (
                    <DefaultButton
                        onClick={fixTokenError}
                        text={loc(accountNotAvailableTryAgainLabel)}
                    />
                )}
                <DefaultButton
                    onClick={openAddAccountsOption}
                    text={loc(accountNotAvailableFixAccountLabel)}
                />
            </DialogFooter>
        </Dialog>
    );

    function closeErrorDialog() {
        logUserResponse('Dismiss');
        setErrorDialogState(true, failureState);
    }

    function openAddAccountsOption() {
        logUserResponse('OpenAddAccountsOption');

        lazyNavigateToAddCalendarPanel.importAndExecute(AddCalendarsMenuItemId.Personal);
        lazyOpenAddCalendarsPane.importAndExecute('OWAAccountsLoadError');
        accountErrorReferral(getAccountsInErrorState().map(account => account.userIdentity));

        // dismiss the dialog
        setErrorDialogState(true, failureState);
    }

    function fixTokenError() {
        logUserResponse('FixTokenError');

        const allAccountsInTokenErrorState = getAccountsInState([
            OWAConnectedAccountState.TransientTokenError,
            OWAConnectedAccountState.FailedToInitializeOWAConfig,
        ]);

        allAccountsInTokenErrorState.map(account =>
            lazyGetAndUpdateAccessToken.importAndExecute(
                account.accountId,
                account.userIdentity,
                account.token
            )
        );
        // dismiss the dialog
        setErrorDialogState(true, failureState);
    }

    function logUserResponse(userResponse: DialogUserResponse) {
        logUsage(
            'owaAccountsLoadErrorResponse',
            {
                userResponse_1: userResponse,
            },
            {
                isCore: true,
            }
        );
    }
});

export default OWAAccountsLoadError;
